import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import API_BASE_URL from "../../../config/api";
import axios from "axios";
import {
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
  FaUsers,
  FaUserTie,
  FaCheck,
  FaCalendarAlt,
  FaLayerGroup,
  FaExternalLinkAlt,
  FaSearch,
  FaSync,
  FaFilter,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

function AdminReportContainer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});

  // Search & Filter & Pagination States
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  // Fetch projects when page, search, or department changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProjects();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [page, search, department]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/projects/all?page=${page}&limit=10&search=${search}&department=${department}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const data = response.data;
      if (response.status === 200) {
        setProjects(data.projects || []);
        setTotalPages(data.totalPages || 1);
        setTotalProjects(data.totalProjects || 0);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleDepartmentChange = (e) => {
    setDepartment(e.target.value);
    setPage(1); // Reset to first page on filter
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(prev => prev + 1);
  };

  // Helper to get preference text
  const getPreferenceText = (preference) => {
    if (!preference) return '';
    if (typeof preference === 'object') {
      return preference.email || (preference.guideId ? `Guide ID: ${preference.guideId}` : 'Unnamed Guide');
    }
    return preference;
  };

  const generateExcel = async () => {
    if (totalProjects === 0) {
      alert("No projects to export.");
      return;
    }

    try {
      const btn = document.getElementById("export-btn");
      if (btn) {
        const originalText = btn.innerHTML;
        btn.innerHTML = `<span class="animate-spin inline-block mr-2">⟳</span> Generating...`;
        btn.disabled = true;
      }

      // Fetch all matching projects by using totalProjects as limit
      // Note: If filters are active (search/department), totalProjects reflects that count
      const response = await axios.get(`${API_BASE_URL}/api/projects/all?page=1&limit=${totalProjects}&search=${search}&department=${department}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const data = response.data;

      const allProjects = data.projects || [];

      // Prepare Data with Header Row
      const wsData = [
        [`Project Allocation Report`],
        [
          "Project Title",
          "Department",
          "Team Members",
          "Assigned Guide",
          "Assigned Co-Guide",
          "Status",
        ],
      ];

      allProjects.forEach((project) => {
        const teamMembers = project.teamMembers ? project.teamMembers.join(", ") : "";
        const assignedGuide = project.guide?.assigned?.email || "Not Assigned";
        const assignedCoGuide = project.coGuides?.assigned?.email || "Not Assigned";

        wsData.push([
          project.projectTitle,
          project.domain?.preference1?.department || "N/A",
          teamMembers,
          assignedGuide,
          assignedCoGuide,
          project.status || "Pending",
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Adjust column widths
      ws["!cols"] = [
        { wch: 30 }, // Title
        { wch: 20 }, // Department
        { wch: 40 }, // Team Members
        { wch: 25 }, // Guide
        { wch: 25 }, // Co-Guide
        { wch: 15 }, // Status
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");
      XLSX.writeFile(wb, `projects_report_${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (error) {
      console.error("Error generating Excel:", error);
      alert("Failed to generate report.");
    } finally {
      const btn = document.getElementById("export-btn");
      if (btn) {
        // Restore original content - hardcoded here for simplicity or could save it
        btn.innerHTML = `<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 384 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H224zm-28 172h-32v32c0 6.6-5.4 12-12 12h-16c-6.6 0-12-5.4-12-12v-32h-32c-6.6 0-12-5.4-12-12v-16c0-6.6 5.4-12 12-12h32v-32c0-6.6 5.4-12 12-12h16c6.6 0 12 5.4 12 12v32h32c6.6 0 12 5.4 12 12v16c0 6.6-5.4 12-12 12zm86-66.2c0 2.8-1.5 5.4-4 6.9L220.8 288l57.2 39.3c2.5 1.5 4 4.1 4 6.9v16.5c0 6.7-7.4 10.7-13.1 7L208 314.1l-60.9 43.6c-5.7 3.7-13.1-.3-13.1-7V334.2c0-2.8 1.5-5.4 4-6.9l57.2-39.3-57.2-39.3c-2.5-1.5-4-4.1-4-6.9v-16.5c0-6.7 7.4-10.7 13.1-7L208 261.9l60.9-43.6c5.7-3.7 13.1.3 13.1 7v16.5z"></path></svg> Export to Excel`;
        btn.disabled = false;
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'modifications': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'guide-assigned': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 font-sans bg-gray-50/50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-4" data-aos="fade-down">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Project Reports
            </h1>
            <p className="mt-1 text-gray-500">Overview of all submitted projects across departments</p>
          </div>
          <button
            id="export-btn"
            className="bg-[#B7202E] text-white px-6 py-2.5 rounded-lg font-semibold shadow-sm hover:bg-[#961925] hover:shadow-md transition-all flex items-center gap-2 "
            onClick={generateExcel}
          >
            <FaFileDownload /> Export to Excel
          </button>
        </div>

        {/* Controls Section: Search & Filter */}
        <div className="mb-6" data-aos="fade-up">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Project Title or Team Members..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all"
              />
            </div>

            {/* Department Filter */}
            <div className="relative min-w-[250px]">
              <FaFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={department}
                onChange={handleDepartmentChange}
                className="w-full pl-12 pr-10 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] appearance-none bg-white transition-all cursor-pointer"
              >
                <option value="">All Departments</option>
                <option value="Computer Engineering">Computer Engineering</option>
                <option value="Information Technology">Information Technology</option>
                <option value="Electronics and Telecommunication Engineering">Electronics and Telecommunication Engineering</option>
                <option value="Mechanical Engineering">Mechanical Engineering</option>
                <option value="Electronics and Computer Engineering">Electronics and Computer Engineering</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchProjects}
              className="px-4 py-2.5 text-gray-600 hover:text-[#B7202E] transition-colors"
              title="Refresh List"
            >
              <FaSync className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Projects List */}
        <div className="space-y-4 min-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500 font-medium">Loading projects...</p>
            </div>
          ) : projects.length > 0 ? (
            projects.map((project, index) => (
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                key={project._id}
              >
                <div
                  className="p-5 flex flex-row justify-between items-start md:items-center cursor-pointer hover:bg-gray-50/50 transition-colors gap-4"
                  onClick={() => toggleProject(project._id)}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-800 hover:text-[#B7202E] transition-colors">
                        {project.projectTitle}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/project/${project._id}`);
                        }}
                        className="text-gray-400 hover:text-[#B7202E]"
                        title="View Details"
                      >
                        <FaExternalLinkAlt className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${getStatusColor(project.status)}`}>
                        {project.status || "Pending"}
                      </span>
                      {project.domain?.preference1?.department && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 flex items-center gap-1">
                          <FaLayerGroup className="w-3 h-3" />
                          {project.domain.preference1.department}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-gray-500">
                    <div className="text-right hidden md:block">
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-0.5">Team Members</div>
                      <div className="text-sm font-semibold text-gray-700">
                        {project.teamMembers?.length || 0} Students
                      </div>
                    </div>
                    <div className={`transform transition-transform duration-300 ${expandedProjects[project._id] ? 'rotate-180' : ''}`}>
                      <FaChevronDown />
                    </div>
                  </div>
                </div>

                <div
                  className={`transition-all duration-300 ease-in-out border-t border-gray-100 bg-gray-50/30 ${expandedProjects[project._id] ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                >
                  <div className="p-6 grid gap-6 md:grid-cols-2">
                    {/* Team Members */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h4 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FaUsers /> Team Members
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {project.teamMembers && project.teamMembers.length > 0 ? (
                          project.teamMembers.map((member, idx) => (
                            <span key={idx} className="bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200 text-sm font-medium text-gray-700">
                              {member}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm italic">No team members listed</span>
                        )}
                      </div>
                    </div>

                    {/* Guides */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h4 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FaUserTie /> Project Guides
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Assigned Guide</p>
                          {project.guide?.assigned?.email ? (
                            <div className="flex items-center gap-2 text-gray-800 font-medium bg-green-50 px-3 py-2 rounded border border-green-100">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              {project.guide.assigned.email}
                            </div>
                          ) : (
                            <div className="text-gray-400 italic text-sm px-1">Not Assigned</div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Assigned Co-Guide</p>
                          {project.coGuides?.assigned?.email ? (
                            <div className="flex items-center gap-2 text-gray-800 font-medium bg-blue-50 px-3 py-2 rounded border border-blue-100">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              {project.coGuides.assigned.email}
                            </div>
                          ) : (
                            <div className="text-gray-400 italic text-sm px-1">Not Assigned</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
              <FaLayerGroup className="mx-auto text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-bold text-gray-800 mb-1">No Projects Found</h3>
              <p className="text-gray-500">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && projects.length > 0 && (
          <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
            <div className="text-sm text-gray-500">
              Showing page <span className="font-bold text-gray-800">{page}</span> of <span className="font-bold text-gray-800">{totalPages}</span>
              <span className="mx-2 text-gray-300">|</span>
              Total <span className="font-bold text-gray-800">{totalProjects}</span> projects
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronLeft className="w-3 h-3" /> Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminReportContainer;
